import type { InvestigationResult, InvestigationStep, ProposedAction } from '@/types/investigation'

export type SourceKind = 'ticket' | 'data' | 'logs' | 'code' | 'agent'

export const SOURCE_LABEL: Record<SourceKind, string> = { ticket: 'TK', data: 'DB', logs: 'LG', code: 'GH', agent: 'AI' }

// The step's tool decides where the evidence came from; anything unrecognised reads as data, since
// the MCP read tools are most of what the agent calls.
export function sourceOf(tool: string): SourceKind {
  if (/support_ticket|ticket/.test(tool)) return 'ticket'
  if (/log|error|replay|betterstack|cloudwatch/.test(tool)) return 'logs'
  if (/code|file|directory|commit|github/.test(tool)) return 'code'
  if (/user_activity/.test(tool)) return 'logs'
  return 'data'
}

export interface MapNode {
  id: string
  // Upstream node ids: a finding's reads, a proposal's or action's findings
  links: string[]
}

export const stepId = (index: number) => `s${index}`
export const findingId = (index: number) => `f${index}`
export const proposalId = (id: string) => `p${id}`
export const actionId = (index: number) => `a${index}`

export interface EvidenceGraph {
  reads: { node: MapNode; index: number; step: InvestigationStep }[]
  skipped: { index: number; step: InvestigationStep }[]
  findings: { node: MapNode; index: number }[]
  proposals: { node: MapNode; proposal: ProposedAction }[]
  actions: { node: MapNode; index: number }[]
  links: Map<string, string[]>
}

export function buildEvidenceGraph(result: InvestigationResult, steps: InvestigationStep[], proposals: ProposedAction[]): EvidenceGraph {
  const reads = steps.map((step, index) => ({ step, index })).filter(({ step }) => !step.isError)
  const readIds = new Set(reads.map(r => stepId(r.index)))
  const findings = (result.findings ?? []).map((f, index) => ({
    node: { id: findingId(index), links: f.evidence.map(stepId).filter(id => readIds.has(id)) },
    index
  }))
  const toFindings = (refs: number[] | undefined) => (refs ?? []).filter(i => i >= 0 && i < findings.length).map(findingId)

  const graph: EvidenceGraph = {
    reads: reads.map(r => ({ ...r, node: { id: stepId(r.index), links: [] } })),
    skipped: steps.map((step, index) => ({ step, index })).filter(({ step }) => step.isError),
    findings,
    proposals: proposals.map(p => ({ proposal: p, node: { id: proposalId(p._id), links: toFindings(p.basedOn) } })),
    actions: result.actions.map((a, index) => ({ index, node: { id: actionId(index), links: toFindings(a.basedOn) } })),
    links: new Map()
  }
  ;[...graph.reads, ...graph.findings, ...graph.proposals, ...graph.actions].forEach(({ node }) => graph.links.set(node.id, node.links))
  return graph
}

// Everything a node rests on, itself included: hovering a proposal lights its findings and the
// reads behind them.
export function upstream(id: string, links: Map<string, string[]>, seen = new Set<string>()): Set<string> {
  if (seen.has(id)) return seen
  seen.add(id)
  ;(links.get(id) ?? []).forEach(next => upstream(next, links, seen))
  return seen
}

export interface Edge {
  from: string
  to: string
}

export function edgesOf(links: Map<string, string[]>): Edge[] {
  return [...links.entries()].flatMap(([to, froms]) => froms.map(from => ({ from, to })))
}

export function isEdgeLit(edge: Edge, lit: Set<string>): boolean {
  return lit.has(edge.from) && lit.has(edge.to)
}
